/* eslint-disable jsdoc/require-jsdoc, @angular-eslint/directive-selector */
import { TemplatePortal } from '@angular/cdk/portal';
import {
    AfterViewInit,
    Component,
    ContentChildren,
    Directive,
    forwardRef,
    inject,
    OnDestroy,
    QueryList,
    TemplateRef,
} from '@angular/core';
import { DrawerPortalName } from '../../drawer-layout.model';
import { DrawerService } from '../../drawer.service';

/**
 * @todo work without selector?
 */
@Directive({
    selector: '_/_',
    standalone: false,
})
export class DrawerTemplateDirective {
    public name: DrawerPortalName;
    constructor(public template: TemplateRef<any>) {}
}
@Directive({
    selector: '[uiDrawerFab]',
    providers: [{ provide: DrawerTemplateDirective, useExisting: forwardRef(() => DrawerFabTemplateDirective) }],
    standalone: false,
})
export class DrawerFabTemplateDirective extends DrawerTemplateDirective {
    name: DrawerPortalName = 'fab';
}
@Directive({
    selector: '[uiDrawerHeader]',
    providers: [{ provide: DrawerTemplateDirective, useExisting: forwardRef(() => DrawerHeaderTemplateDirective) }],
    standalone: false,
})
export class DrawerHeaderTemplateDirective extends DrawerTemplateDirective {
    name: DrawerPortalName = 'customHeader';
}
@Directive({
    selector: '[uiToolbarTitle]',
    providers: [
        { provide: DrawerTemplateDirective, useExisting: forwardRef(() => DrawerToolbarTitleTemplateDirective) },
    ],
    standalone: false,
})
export class DrawerToolbarTitleTemplateDirective extends DrawerTemplateDirective {
    name: DrawerPortalName = 'toolbarTitle';
}
@Directive({
    selector: '[uiToolbarSubTitle]',
    providers: [
        { provide: DrawerTemplateDirective, useExisting: forwardRef(() => DrawerToolbarSubTitleTemplateDirective) },
    ],
    standalone: false,
})
export class DrawerToolbarSubTitleTemplateDirective extends DrawerTemplateDirective {
    name: DrawerPortalName = 'toolbarSubTitle';
}
@Directive({
    selector: '[uiToolbarActions]',
    providers: [
        { provide: DrawerTemplateDirective, useExisting: forwardRef(() => DrawerToolbarActionsTemplateDirective) },
    ],
    standalone: false,
})
export class DrawerToolbarActionsTemplateDirective extends DrawerTemplateDirective {
    name: DrawerPortalName = 'toolbarActions';
}

/**
 * Projects content to different places in `ui-drawer-layout`
 */
@Component({
    selector: 'ui-drawer-portals',
    template: ``,
    standalone: false,
})
export class DrawerPortalsComponent implements AfterViewInit, OnDestroy {
    /** All templates provided by `DrawerTemplateDirective`s */
    @ContentChildren(DrawerTemplateDirective) private templates: QueryList<DrawerTemplateDirective>;

    private drawerService = inject(DrawerService);

    /**
     * NG Hook
     *
     * Initialises portals
     */
    ngAfterViewInit() {
        this.setDrawerPortals();
    }

    /**
     * NG Hook
     */
    ngOnDestroy() {
        this.setDrawerPortals(true);
    }

    // @LogMethod({ ignoreProperties: ['reset'] })
    private setDrawerPortals(reset = false) {
        // `templates` is wired via `@ContentChildren` and only becomes a `QueryList`
        // *after* `ngAfterViewInit`. When the user rapidly switches routes/tabs the
        // host component can be destroyed before its content has been queried, in which
        // case `ngOnDestroy` runs while `templates` is still `undefined`. Calling
        // `.forEach` on `undefined` then throws a `TypeError`, which surfaces as an
        // uncaught promise rejection in `NgZone` — the same handler that drives change
        // detection. After the zone has dispatched the unhandled error once, change
        // detection silently stops processing UI events on that zone, leaving the user
        // staring at a white screen even though network/WS activity continues.
        // Guarding with `?.` keeps the cleanup a no-op in that race instead.
        this.templates?.forEach(({ name, template }) => {
            const portalValue = reset ? undefined : new TemplatePortal(template, undefined, {});
            if (template) {
                this.drawerService.setDrawerPortal(name, portalValue);
            }
        });
        return this.drawerService.getDrawerPortalState();
    }
}
